import type { ErrorEvent } from '@sentry/astro';

type Frame = { function?: string; filename?: string };

/**
 * `event.contexts.browser` is derived server-side from the request's
 * User-Agent, so it is empty inside the browser SDK's `beforeSend`. Matching
 * the engine therefore has to use `navigator.userAgent`.
 */
const UNSUPPORTED_ENGINE_UA = /NetCast|SMART-TV|SmartTV|Tizen|Web0S|WebOS|SeaMonkey/i;

/** Scripts the TV browser injects into every page (`lgcrw_browser_*`). */
const TV_BROWSER_INJECTION_FRAME = /^lgcrw_/;

/** Chrome on iOS (WKWebView) evaluates its own scripts against the page. */
const CHROME_IOS_UA = /\bCriOS\//;

const CROSS_ORIGIN_FRAME_ACCESS = /^Blocked a frame with origin .* from accessing a cross-origin frame/;

/**
 * True for errors thrown by code we do not ship:
 *  - unsupported smart-TV / legacy engines (and their injected `lgcrw_*` scripts)
 *  - Chrome iOS's injected scripts, which are attributed to the page URL with
 *    minified names and throw on cross-origin frame access or recurse until the
 *    stack overflows. We never touch `contentWindow`/`window.top`, and our own
 *    bundles are always served from `/_astro/*.js`.
 */
export function isThirdPartyInjectionNoise(event: ErrorEvent, userAgent: string): boolean {
  if (UNSUPPORTED_ENGINE_UA.test(userAgent)) return true;

  const exc = event.exception?.values?.[0];
  const frames: Frame[] = exc?.stacktrace?.frames ?? [];
  if (frames.some((f) => TV_BROWSER_INJECTION_FRAME.test(f.function ?? ''))) return true;

  if (!CHROME_IOS_UA.test(userAgent)) return false;

  if (exc?.type === 'SecurityError' && CROSS_ORIGIN_FRAME_ACCESS.test(exc.value ?? '')) return true;

  const hasOwnBundleFrame = frames.some((f) => /\/_astro\//.test(f.filename ?? ''));
  if (exc?.type === 'RangeError' && /Maximum call stack size exceeded/.test(exc.value ?? '')) {
    return frames.length > 0 && !hasOwnBundleFrame;
  }
  return false;
}
